import axios from 'axios';

/**
 * 版本服务
 * 用于获取服务器部署的版本号，并进行版本比较
 */

// 从 Vite 注入的当前构建版本
declare const __APP_VERSION__: string;

/**
 * 将版本字符串转换为比较用的数字数组
 * @param version 版本字符串，如 '1.0.1'
 * @returns 比较数组，如 [1, 0, 1]
 */
const parseVersionToArray = (version: string): number[] => {
  return version.split('.').map(part => {
    const num = parseInt(part, 10);
    return isNaN(num) ? 0 : num;
  });
};

class VersionService {
  private cachedVersion: string | null = null;

  /**
   * 获取服务器当前版本号
   * 通过请求根路径 '/' 解析 HTML 中的 meta version
   * @returns 版本号字符串，如 '1.0.1'
   */
  async getVersion(): Promise<string> {
    try {
      const response = await axios.get('/', {
        timeout: 5000,
        responseType: 'text'
      });

      const html = response.data as string;
      const version = this.parseVersion(html);
      
      if (version) {
        this.cachedVersion = version;
        return version;
      }

      return 'unknown';
    } catch (error) {
      console.error('获取版本号失败:', error);
      return 'unknown';
    }
  }

  /**
   * 获取本地版本号
   * 从当前页面 <meta name="version"> 标签获取
   * @returns 本地构建版本号
   */
  getLocalVersion(): string {
    const metaVersion = document.querySelector('meta[name="version"]');
    if (metaVersion) {
      const content = metaVersion.getAttribute('content');
      if (content && content.trim()) {
        return content.trim();
      }
    }
    return 'unknown';
  }

  /**
   * 比较两个版本号
   * @param serverVersion 服务器版本
   * @param localVersion 本地版本
   * @returns 1: 服务器版本更新, -1: 本地版本更新, 0: 版本相同
   */
  compareVersions(serverVersion: string, localVersion: string): number {
    if (serverVersion === 'unknown' || localVersion === 'unknown') {
      return 0;
    }

    const serverParts = parseVersionToArray(serverVersion);
    const localParts = parseVersionToArray(localVersion);

    // 补齐数组长度
    const maxLength = Math.max(serverParts.length, localParts.length);
    for (let i = 0; i < maxLength; i++) {
      const serverPart = serverParts[i] || 0;
      const localPart = localParts[i] || 0;
      
      if (serverPart > localPart) {
        return 1;
      }
      if (serverPart < localPart) {
        return -1;
      }
    }

    return 0;
  }

  /**
   * 检查服务器版本是否更新
   * @param serverVersion 服务器版本
   * @returns true: 服务器版本更新，需要刷新
   */
  isServerNewer(serverVersion: string): boolean {
    // 版本号无效时不进行刷新判断
    if (!serverVersion || serverVersion === 'unknown') {
      console.log('[VersionContext] 服务器版本无效，跳过刷新检查');
      return false;
    }
    
    // 检查版本号格式是否有效（必须是 x.x.x 格式）
    const versionRegex = /^\d+\.\d+\.\d+$/;
    if (!versionRegex.test(serverVersion)) {
      console.log(`[VersionContext] 服务器版本格式无效: ${serverVersion}，跳过刷新检查`);
      return false;
    }
    
    const localVersion = this.getLocalVersion();
    
    // 本地版本无效时也不刷新
    if (!localVersion || localVersion === 'unknown') {
      console.log('[VersionContext] 本地版本无效，跳过刷新检查');
      return false;
    }
    
    // 检查本地版本格式是否有效
    if (!versionRegex.test(localVersion)) {
      console.log(`[VersionContext] 本地版本格式无效: ${localVersion}，跳过刷新检查`);
      return false;
    }
    
    const result = this.compareVersions(serverVersion, localVersion);
    console.log(`[VersionContext] 版本比较结果: ${serverVersion} vs ${localVersion} = ${result}`);
    
    return result === 1;
  }

  /**
   * 从 HTML 内容中解析版本号
   * @param html HTML 字符串
   * @returns 版本号字符串
   */
  private parseVersion(html: string): string | null {
    // 匹配 <meta name="version" content="1.0.1">
    const regex = /<meta\s+name=["']version["']\s+content=["']([^"']+)["']/i;
    const match = html.match(regex);
    
    if (match && match[1]) {
      return match[1];
    }

    // 备用方案：匹配任意 meta name=version
    const backupRegex = /meta\s+[^>]*name=["']version["'][^>]*content=["']([^"']+)["']/i;
    const backupMatch = html.match(backupRegex);
    
    if (backupMatch && backupMatch[1]) {
      return backupMatch[1];
    }

    return null;
  }

  /**
   * 清除版本号缓存
   */
  clearCache(): void {
    this.cachedVersion = null;
  }
}

export default new VersionService();
